import {
  ListUsersQueryVariables,
  User,
  UserByHabitatQueryVariables,
} from '@akin-home/amplify';
import { sortByName } from '@akin-home/arrays';
import { UserModel } from '@akin-home/user';
import {
  getUserQuery,
  graphqlUserToModel,
  listAllUsersByHabitatQuery,
  listAllUsersByUsernameQuery,
  listAllUsersQuery,
  listUsersQuery,
} from '@akin-home/users/frontend';
import { createQueryKeySet } from '@akin-home/utils/react-query';
import { useInfiniteQuery, useQuery } from 'react-query';

export * from './useCreateUser';
export * from './useDeleteUser';
export * from './useUpdateUser';

export const userQueryKeys = createQueryKeySet({ key: 'users' });

export const useListUsers = <T>({
  params,
  options,
}: {
  params?: ListUsersQueryVariables;
  options?: T;
}) =>
  useInfiniteQuery(
    userQueryKeys.pages(params),
    async ({ pageParam }) =>
      listUsersQuery({ nextToken: pageParam, ...params }),
    { ...options, getNextPageParam: (res) => res.data.listUsers?.nextToken }
  );

/** Hook to fetch all users */
export const useListAllUsers = (
  params?: ListUsersQueryVariables,
  options?: any
) =>
  useQuery(
    userQueryKeys.list(params),
    async () => sortByName(await listAllUsersQuery(params)) as User[],
    options
  );

export const useListAllUsersByHabitat = <T>(
  params: UserByHabitatQueryVariables,
  options?: T
) =>
  useQuery<User[], any, UserModel[]>(
    userQueryKeys.list(params),
    async () => sortByName(await listAllUsersByHabitatQuery(params)) as User[],
    {
      select: (data) => data.map(graphqlUserToModel),
      ...options,
    }
  );

export const useListHabitatUsers = (
  habitatID?: string,
  additional_params?: ListUsersQueryVariables
) => {
  /** Only do the query if the habitatID is actually specified */
  return useListAllUsersByHabitat(
    {
      habitatID: habitatID as string,
      ...additional_params,
    },
    { enabled: !!habitatID }
  );
};

export const useListUsersByUsername = <T>(username?: string, options?: T) => {
  /** Only do the query if the habitatID is actually specified */
  return useListAllUsers(
    {
      filter: {
        username: { eq: username },
      },
    },
    { enabled: !!username, ...options }
  );
};

export const useUserByUsername = ({ username }: { username?: string }) => {
  return useQuery(
    userQueryKeys.list({ username }),
    async () =>
      await listAllUsersByUsernameQuery({ username: username as string }),
    { enabled: !!username, select: (data) => data?.[0] }
  );
};

export const useGetUserQuery = (id: string) =>
  useQuery(userQueryKeys.detail(id), async () => getUserQuery({ id }), {
    select(data) {
      return data.data.getUser;
    },
  });
